import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import { ServiceOrder } from 'src/app/interfaces/service-order';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { FormService } from 'src/app/services/form.service';
import { ServiceOrdersService } from 'src/app/services/service-orders.service';
import { ToastMessageService } from 'src/app/widgets/toast-message/toast-message.service';

@Component({
  selector: 'app-service-order-more-info-form',
  templateUrl: './service-order-more-info-form.component.html',
  styleUrls: ['./service-order-more-info-form.component.scss']
})
export class ServiceOrderMoreInfoFormComponent implements OnInit, OnDestroy {

  @Input() soHeadId: number;

  // claims
  canEdit = this._authSvc.hasClaim('ServiceOrders', 'Edit');

  // states
  formProcessing = false;
  isSubmitted = false;
  loading = true;

  // error messages
  formErrors: any = null;
  fieldErrors: any = {};

  // menu options data
  custFollowUpCodes = null;

  // form group
  form: UntypedFormGroup = this._fb.group({
    Id: [null],
    ConfidentialNotes: [null],
    Notes: [null],
    CustomerFollowUpCodes: this._fb.array([])
  });

  // endpoints
  private _detailEp = `${this._apiSvc.endpoints.sales}/api/serviceorder/details`;
  private _followUpCodeEp = `${this._apiSvc.endpoints.crm}/api/CustomerFollowUp`;
  
  // subscriptions
  private _forkJoinSub: Subscription;
  private _svcOrderSub: Subscription;

  constructor(
    private _authSvc: AuthService,
    private _apiSvc: ApiService,
    private _fb: UntypedFormBuilder,
    private _formSvc: FormService,
    private _svcOrderSvc: ServiceOrdersService,
    private _toastMsgSvc: ToastMessageService
  ) { }

  ngOnInit(): void {

    this._getData();
  
    this._svcOrderSub = this._svcOrderSvc.ServiceOrder
      .subscribe((res: ServiceOrder) => {
        if (res && res.SoStatusCode === 3 || res.SoStatusCode === 4) {
          this.canEdit = false;
          this.form.disable();
        }
      });
  
  }

  ngOnDestroy(): void {

    if (this._forkJoinSub) {
      this._forkJoinSub.unsubscribe();
    }

    this._svcOrderSub.unsubscribe();

  }

  /**
   * Get details data
   */
  private _getData(): void {

    const obs = {
      followUpCodes: this._apiSvc.search(this._followUpCodeEp, {sort: 'Code-asc'}),
      details: this._apiSvc.get(`${this._detailEp}/${this.soHeadId}`)
    };

    this._forkJoinSub = forkJoin(obs).subscribe((res: any) => {
      this.custFollowUpCodes = res.followUpCodes.Data;
      this._patchForm(res.details);
    });

  }

  /**
   * Patch form values
   * @param values
   */
  private _patchForm(values: any): void {

    const patchValue = this._formSvc.getPatchValue(this.form.controls, values);

    this.form.patchValue(patchValue);

    if (patchValue.CustomerFollowUpCodes.length === 0) {
      this.addFollowUpCode();
    } else {
      values.CustomerFollowUpCodes.forEach((code: any) => {
        this.addFollowUpCode(code);
      });
    }

    this.loading = false;

    if (!this.canEdit) {
      this.form.disable();
    }

  }
  
  /**
   * Create follow up code form group
   * @param code
   */
  createFollowUpFields(code: any = null): any {
    return this._fb.group({
      Id: [code ? code.Id : null],
      Date: [code ? code.Date : null]
    });
  }

  /**
   * Get follow up codes array
   */
  get FollowUpCodes() {
    return this.form.get('CustomerFollowUpCodes') as UntypedFormArray;
  }

  /**
   * Add form group to follow up codes
   * @param code
   */
  addFollowUpCode(code: any = null): void {
    this.FollowUpCodes.push(this.createFollowUpFields(code));
  }

  /**
   * Remove specified form group from follow up codes
   * @param index
   */
  removeFollowUpCode(index: number): void {

    this.FollowUpCodes.removeAt(index);
    if (this.FollowUpCodes.length === 0) {
      this.addFollowUpCode();
    }

  }

  /**
   * Get request payload
   */
  private get _Payload(): any {

    const payload = JSON.parse(JSON.stringify(this.form.value));

    const followUpCodes = [];
    payload.CustomerFollowUpCodes.forEach((code: any) => {
      if (code.Id || code.Date) {
        followUpCodes.push(code);
      }
    });

    // update payload with follow up codes
    if (followUpCodes.length > 0) {
      payload.CustomerFollowUpCodes = followUpCodes;
    } else {
      delete payload.CustomerFollowUpCodes;
    }

    return payload;

  }

  /**
   * Form submission event handler
   * @param e
   */
  onSubmit(e: Event): void {

    e.preventDefault();

    this.isSubmitted = true;

    if (!this.form.valid || this.formProcessing) {
      return;
    }

    // clear errors
    this.formErrors = null;
    this.fieldErrors = {};

    this.formProcessing = true;

    this._apiSvc.update(`${this._detailEp}/${this.soHeadId}`, this._Payload)
      .subscribe(() => {

        this._toastMsgSvc.send({
          type: 'success',
          message: 'Service order details saved',
          icon: 'check_circle_outline'
        });

        this.formProcessing = false;

      }, (err: any) => {

        const errors = this._formSvc.processFormErrors(err);

        if (errors.formErrors) {
          this.formErrors = errors.formErrors;
        }

        if (errors.fieldErrors) {
          this.fieldErrors = errors.fieldErrors;
        }

        this.formProcessing = false;

      });

  }

}
